import moment from 'moment';

/**
 * Normalizes a date string to ensure proper format for moment.js parsing
 * Handles dates like "2025-1-10 12:00:00" by padding single digits
 * @param {string} dateString - The date string to normalize
 * @returns {string} - Normalized date string in YYYY-MM-DD HH:mm:ss or YYYY-MM-DD format
 */
export const normalizeDateString = (dateString) => {
  if (!dateString || typeof dateString !== 'string') {
    return dateString;
  }

  // Handle dates like "2025-1-10 12:00:00" by padding single digits
  const dateTimeMatch = dateString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})\s+(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (dateTimeMatch) {
    const [, year, month, day, hour, minute, second] = dateTimeMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')} ${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${second.padStart(2, '0')}`;
  }

  // Handle dates like "2025-1-10"
  const dateMatch = dateString.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (dateMatch) {
    const [, year, month, day] = dateMatch;
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
  }

  return dateString;
};

/**
 * Safely parses a date string using moment.js with proper format handling
 * @param {string} dateString - The date string to parse
 * @param {string} format - Optional format string for strict parsing
 * @returns {moment.Moment} - Parsed moment object
 */
export const safeMomentParse = (dateString, format = null) => {
  if (!dateString) return moment.invalid();

  const normalizedDate = normalizeDateString(dateString);
  
  if (format) {
    return moment(normalizedDate, format, true);
  }
  
  // Try common formats first
  const formats = [
    'YYYY-MM-DD HH:mm:ss',
    'YYYY-MM-DD',
    'YYYY-MM-DDTHH:mm:ss.SSSZ',
    'YYYY-MM-DDTHH:mm:ssZ',
    'YYYY-MM-DDTHH:mm:ss',
    'MMMM DD, YYYY h:mm A',  // June 03, 2025 2:58 PM
    'MMMM D, YYYY h:mm A',   // June 3, 2025 2:58 PM
    'MMM DD, YYYY h:mm A',   // Jun 03, 2025 2:58 PM
    'MMM D, YYYY h:mm A',    // Jun 3, 2025 2:58 PM
    'MM/DD/YYYY h:mm A',     // 06/03/2025 2:58 PM
    'MM/DD/YYYY HH:mm',      // 06/03/2025 14:58
    'DD/MM/YYYY h:mm A',     // 03/06/2025 2:58 PM
    'DD/MM/YYYY HH:mm'       // 03/06/2025 14:58
  ];
  
  for (const fmt of formats) {
    const parsed = moment(normalizedDate, fmt, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }
  
  // Last resort: try to parse as ISO string or return invalid moment
  try {
    const parsed = moment(normalizedDate, moment.ISO_8601, true);
    if (parsed.isValid()) {
      return parsed;
    }
    
    // If still invalid, try without strict mode as last resort
    const fallbackParsed = moment(normalizedDate);
    if (fallbackParsed.isValid()) {
      return fallbackParsed;
    }
    
    return moment.invalid();
  } catch (error) {
    console.warn('Failed to parse date:', normalizedDate, error);
    return moment.invalid();
  }
};

/**
 * Formats a date string safely, handling non-standard formats
 * @param {string} dateString - The date string to format
 * @param {string} outputFormat - The desired output format
 * @param {string} fallback - Fallback value if parsing fails
 * @returns {string} - Formatted date string or fallback
 */
export const safeDateFormat = (dateString, outputFormat = 'MM/DD/YYYY hh:mm A', fallback = '-') => {
  const parsedDate = safeMomentParse(dateString);
  return parsedDate.isValid() ? parsedDate.format(outputFormat) : fallback;
};
